import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // Keep the two typographic Forex model galleries in sync on initial render.
    if (window.location.pathname.includes('/products/panneau-rigide-typographique-signature')) {
      const picker = target?.querySelector('variant-picker');
      const model = picker?.getAllSelectedOptions?.().find((o) => o.name.toLowerCase() === 'modèle');
      this.#syncTypographicDesign(this, model?.value);
    }
    if (window.location.pathname.includes('/products/panneau-rigide-emotion')) {
      const picker = target?.querySelector('variant-picker');
      const model = picker?.getAllSelectedOptions?.().find((o) => o.name.toLowerCase() === 'modèle');
      this.#syncEmotionDesign(this, model?.value);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Shopify can return images for the previous design even when the
        // second option (Modèle) has changed. Correct slides and thumbnails
        // using the filename convention, without modifying other products.
        if (window.location.pathname.includes('/products/panneau-rigide-typographique-signature')) {
          const model = event.selectedOptions?.find((o) => o.name.toLowerCase() === 'modèle');
          this.#syncTypographicDesign(newMediaGallery, model?.value);
        }
        if (window.location.pathname.includes('/products/panneau-rigide-emotion')) {
          const model = event.selectedOptions?.find((o) => o.name.toLowerCase() === 'modèle');
          this.#syncEmotionDesign(newMediaGallery, model?.value);
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Map the three slides, thumbnails and zoom previews to the selected
   * typographic design. The filenames were supplied in the product's Shopify
   * media; the model selection is option 2, while option 1 is the format.
   * @param {Element} gallery
   * @param {string | undefined} modelValue
   */
  #syncTypographicDesign(gallery, modelValue) {
    if (!modelValue) return;
    const model = modelValue.trim().toLowerCase().includes('old money') ? '2' : '1';
    const pattern = /panneau-forex-essentiel-model[12](?=-[123][^/?]*\.webp)/gi;

    for (const media of gallery.querySelectorAll('img, source')) {
      for (const attribute of ['srcset', 'src', 'data-srcset', 'data-src']) {
        const current = media.getAttribute(attribute);
        if (!current || !current.includes('panneau-forex-essentiel-model')) continue;
        const next = current.replace(pattern, 'panneau-forex-essentiel-model' + model);
        if (next !== current) media.setAttribute(attribute, next);
      }
    }
  }

  /**
   * Keep the three Émotion slides, thumbnails and zoom previews in sync
   * with the first product option (Modèle 1 or Modèle 2).
   * @param {Element} gallery
   * @param {string | undefined} modelValue
   */
  #syncEmotionDesign(gallery, modelValue) {
    if (!modelValue) return;
    const model = modelValue.trim().toLowerCase().includes('2') ? '2' : '1';
    const pattern = /panneau-forex-emotion-model[12](?=-[123][^/?]*\.webp)/gi;

    for (const media of gallery.querySelectorAll('img, source')) {
      for (const attribute of ['srcset', 'src', 'data-srcset', 'data-src']) {
        const current = media.getAttribute(attribute);
        if (!current || !current.includes('panneau-forex-emotion-model')) continue;
        const next = current.replace(pattern, 'panneau-forex-emotion-model' + model);
        if (next !== current) media.setAttribute(attribute, next);
      }
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
